/**
 * Recent activity modal — lists the current user's audit log entries.
 * Triggered from the avatar menu in the navbar.
 */
import { myAuditLog } from '../api/audit.js';
import { auth } from '../auth/state.js';
import { toast } from './toast.js';
import { listSkeleton } from './skeleton.js';
import { emptyState } from './emptyState.js';
import { friendlyMessage } from '../api/client.js';

const ACTION_LABELS = {
  'layer.upload':   'Uploaded layer',
  'layer.share':    'Shared layer to catalog',
  'layer.unshare':  'Made layer private',
  'layer.delete':   'Deleted layer',
  'story.share':    'Published story',
  'story.unshare':  'Unpublished story',
};

function formatAction(a) { return ACTION_LABELS[a] || a; }

function relativeTime(iso) {
  const t = new Date(iso).getTime();
  const diff = (Date.now() - t) / 1000;
  if (diff < 60)         return 'just now';
  if (diff < 3600)       return `${Math.round(diff / 60)} min ago`;
  if (diff < 86400)      return `${Math.round(diff / 3600)} hr ago`;
  if (diff < 86400 * 7)  return `${Math.round(diff / 86400)} d ago`;
  return new Date(iso).toLocaleDateString();
}

export function openAuditLogModal() {
  if (!auth.isAuthenticated()) { toast('Sign in first'); return; }
  ensureMounted();
  document.getElementById('auditModal').classList.add('show');
  load();
}

export function closeAuditLogModal() {
  document.getElementById('auditModal')?.classList.remove('show');
}

function ensureMounted() {
  if (document.getElementById('auditModal')) return;
  document.body.insertAdjacentHTML('beforeend', `
    <div class="modal-bg" id="auditModal">
      <div class="modal" style="width:520px;padding:0;max-width:96vw">
        <div style="display:flex;align-items:baseline;justify-content:space-between;padding:18px 22px 12px;border-bottom:1px solid var(--border)">
          <h3 style="margin:0">Recent activity</h3>
          <button class="rpanel-close" data-action="closeAuditLogModal">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div id="auditBody" style="padding:14px 6px 18px;max-height:60vh;overflow-y:auto"></div>
      </div>
    </div>
    <style>
      .au-row {
        display: grid;
        grid-template-columns: 6px 1fr auto;
        gap: 12px; align-items: baseline;
        padding: 12px 16px;
        border-bottom: 1px solid var(--border);
      }
      .au-row:last-child { border-bottom: none; }
      .au-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--primary); margin-top: 6px; }
      .au-action {
        font-size: 13px; color: var(--text); font-weight: 500; letter-spacing: -0.005em;
      }
      .au-resource {
        font-size: 11.5px; color: var(--text3); margin-top: 2px;
        font-family: var(--font-display); font-style: italic;
      }
      .au-time {
        font-family: var(--mono); font-size: 10.5px; color: var(--text3);
        font-feature-settings: 'tnum'; white-space: nowrap;
      }
    </style>
  `);
  document.getElementById('auditModal').addEventListener('click', e => {
    if (e.target.id === 'auditModal') closeAuditLogModal();
  });
}

async function load() {
  const body = document.getElementById('auditBody');
  body.innerHTML = listSkeleton(5);
  try {
    const rows = await myAuditLog(100);
    if (rows.length === 0) {
      body.innerHTML = emptyState({
        title: 'Nothing yet',
        body: 'Actions like uploading, sharing, deleting, or publishing show up here.',
      });
      return;
    }
    body.innerHTML = rows.map(r => `
      <div class="au-row">
        <div class="au-dot"></div>
        <div>
          <div class="au-action">${escape(formatAction(r.action))}</div>
          <div class="au-resource">${escape(describeResource(r))}</div>
        </div>
        <div class="au-time">${escape(relativeTime(r.created_at))}</div>
      </div>
    `).join('');
  } catch (e) {
    body.innerHTML = `<div class="alert alert-error" style="margin:6px 16px">${escape(friendlyMessage(e))}</div>`;
  }
}

function describeResource(row) {
  const meta = row.metadata_ || {};
  if (meta.name)   return meta.name;
  if (meta.title)  return meta.title;
  if (row.resource_type && row.resource_id) return `${row.resource_type} ${row.resource_id}`;
  return '—';
}

function escape(s) {
  return String(s ?? '').replace(/[&<>"]/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' }[c]));
}
